// Edit this file to publish news / patch notes. Newest first.
//
// The News page is a version ledger: ONE post per version, and every post is
// one optional title followed by one bulleted list of what changed. Add the
// title when the update has a focus worth naming; otherwise the bullets stand
// on their own under the version bar. Never two headings in a post: if a
// version needs more, it gets more bullets, not more posts.
export interface NewsItem {
  date: string; // YYYY-MM-DD
  title?: string; // optional headline, for an update with one clear focus
  // What changed, one bullet per thing, from the player's point of view. New
  // posts always use this; `body` alone is only for older posts and renders
  // as a single bullet.
  bullets?: string[];
  body?: string; // legacy: a paragraph instead of bullets (rendered as one bullet)
  tag?: string; // optional small label, e.g. "Update", "Fix"
  // The game version this post shipped with (e.g. "0.16.0"). It is the ledger's
  // heading; new posts should always set it (it pairs with the package.json
  // bump). Old posts without one are grouped by tag instead.
  version?: string;
  // Stable URL slug; derived from the title, else from the date and version,
  // if omitted. Set it explicitly when a post has no title.
  slug?: string;
}

// Stable URL slug for a post. Prefer an explicit `slug` (so editing a title
// never breaks the URL); otherwise derive one from the title, or, for a post
// without one, from its date and version.
export function newsSlug(item: NewsItem): string {
  if (item.slug) return item.slug;
  const base = item.title ?? `${item.date}${item.version ? "-v" + item.version : ""}`;
  return base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// What to call a post where a heading is needed (the post page, link
// previews): its title, else "Version x.y.z", else its date.
export function newsHeading(item: NewsItem): string {
  return item.title ?? (item.version ? `Version ${item.version}` : item.date);
}

// The post's bullets. Older posts wrote a paragraph instead; it becomes the one
// bullet, so every post reads the same way.
export function newsBullets(item: NewsItem): string[] {
  if (item.bullets?.length) return item.bullets;
  return item.body?.trim() ? [item.body.trim()] : [];
}

export function findNewsBySlug(slug: string): NewsItem | undefined {
  return NEWS.find((n) => newsSlug(n) === slug);
}

// Plain-text summary for meta description / link previews.
export function newsSummary(item: NewsItem, max = 155): string {
  const s = newsBullets(item).join(" · ");
  return s.length <= max ? s : s.slice(0, max - 1).trimEnd() + "…";
}

// "2 Sep 2026"
export function newsDateLabel(date: string): string {
  return new Date(date + "T12:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

export const NEWS: NewsItem[] = [
  {
    date: "2026-10-20",
    version: "0.40.0",
    title: "Play Now and the Royale",
    bullets: [
      "Play Now: one room that never closes. Jump in any time, and start playing when the next round starts.",
      "The Royale: Runs every 30 min. 30 worms where every three rounds the bottom of the table drops out, until two are left for the final. Win it for Golden Eggs and glory!",
      "While waiting for a Royale or a ranked match you can now Play Now while you wait.",
      "Game Rooms are rebuilt, and phones now force landscape mode.",
    ],
  },
  {
    date: "2026-10-02",
    version: "0.30.0",
    title: "Ranked, achievements, and mobile friendly arenas",
    bullets: [
      "Ranked mode is live with two ladders: 1 vs 1 and 6-player free-for-all.",
      "New achievements and they now have levels, from I up to V, and every level you reach pays a few Golden Eggs.",
      "2 new arena shapes made for phones: a wide rectangle and a stadium. They are now the standard maps and work both in vertical and horizontal mode.",
      "Arena sizes have changed. Mainly to work better for phones but also to keep the games short and sweet.",
      "Some overall design changes and bug fixes.",
    ],
  },
  {
    date: "2026-09-20",
    version: "0.24.0",
    title: "Replays are live",
    bullets: [
      "Every online match is now recorded and you can watch the replay at the end of a match.",
      "Your profile's match list has a link to the replay that lasts for 30 days. The link works for anyone you send it to, so go ahead and show off!",
    ],
  },
  {
    date: "2026-09-02",
    version: "0.18.0",
    title: "Celebrations, celebrated",
    bullets: [
      "Fireworks reworked: rockets fire ahead of your worm and burst across the arena, trailing you as you drive.",
      "Bigger crowns, a shinier golden trophy, and a gunslinger that never reloads.",
      "Every celebration hides a crash finale. Trying one out in the shop lets you crash on purpose to see it.",
    ],
  },
  {
    date: "2026-08-25",
    version: "0.17.0",
    title: "The shop is open",
    slug: "shop-launch",
    bullets: [
      "Golden Eggs are the currency. Grab a pack, then spend eggs on victory celebrations, worm heads, arena styles, and flags bolder than a country.",
      "Everything is cosmetic. Nothing changes gameplay, and your regular country flag stays free forever.",
      "Rarer means shinier.",
    ],
  },
  {
    date: "2026-08-23",
    version: "0.16.0",
    title: "A proper finale",
    slug: "winner-screens-and-match-stats",
    bullets: [
      "Every round ends on a podium card with the current top three.",
      "When the match wraps, your rating counts up live and a promotion detonates your badge into the next tier.",
      "Match stats: how far you wormed, power-ups grabbed, crashes you caused, gaps you threaded.",
      "Replays fit the screen again, and the lobby shows which arena you are about to play and what it takes to win.",
    ],
  },
  {
    date: "2026-08-20",
    version: "0.15.0",
    title: "Ranks and the leaderboard",
    slug: "rank-badges-ladder",
    bullets: [
      "Sign in and play any matchmaking box to earn a skill rating, a tier from your very first match, and a spot on the worldwide leaderboard.",
      "Rank badges from Bronze to Wormlord, shown on the leaderboard and next to your name on the home page.",
      "Matches still go full screen when they start and step back out when they end. A switch in Settings turns that off.",
      "Rounds-based matches show the real target in the HUD, and knocked-out players are dimmed on the live scoreboard.",
    ],
  },
  {
    date: "2026-08-18",
    version: "0.14.1",
    slug: "join-our-discord",
    bullets: [
      "We have a Discord. Find people to play with and tell us what to build next: the icon is in the footer, or go to discord.gg/uqMGcrcJWN.",
    ],
  },
  {
    date: "2026-07-17",
    version: "0.14.0",
    slug: "player-country-flags",
    bullets: [
      "Fly your flag: tap the flag next to your name, pick your country, and your worm carries it into every match.",
    ],
  },
  {
    date: "2026-07-13",
    version: "0.13.0",
    title: "Power-ups, reworked",
    slug: "power-ups-reworked",
    bullets: [
      "A new look: each power-up sits on a coin with a coloured rim. Green helps you, red hurts rivals, blue hits everyone.",
      "A ring on the worm's head shows each active power-up and drains as it wears off.",
      "Grabbing a red one fires a missile at every rival; green and blue vanish in a puff of dust.",
      "Wall hack lights the arena walls green while they are open.",
    ],
  },
  {
    date: "2026-07-04",
    tag: "Update",
    title: "Make your own lobby",
    slug: "custom-lobbies",
    bullets: [
      "Create a private lobby and invite with a link, a QR code, or a short code.",
      "Set the arena, the winning condition, worm speed, and power-ups. Chat while you wait.",
      "Browse open public lobbies, or jump in with a code.",
    ],
  },
  {
    date: "2026-06-17",
    tag: "Update",
    title: "Online multiplayer is live",
    slug: "online-multiplayer-is-live-beta",
    bullets: [
      "Real-time online matches: last worm standing wins.",
      "Optional accounts: sign in with Google, Discord, Facebook, or an email link to keep your name and stats.",
      "Install Achtung Royale as an app on your phone or desktop. It works offline for local play.",
    ],
  },
];
